import React, { useEffect, useState } from 'react';

import { ocppMessageType } from '../../config/enum';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAllCharingPoints } from '../../redux/slices/ChargingPointSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import MyButton from '../../shared-components/button/Button';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SelectField from '../../shared-components/select-field/SelectField';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import ocppEvents from '../../utils/api/ocpp-events';

interface RemoteStartTransactionProperties {
  devices: string;
  connectorId: string;
}

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * OCPP message command clear cache
 */
function RemoteStartTransaction() {
  const dispatch = useAppDispatch();
  const { chargingPoints } = useAppSelector((state) => state.chargingPoint);

  const [configuration, setConfiguration] = useState<RemoteStartTransactionProperties>({
    devices: '',
    connectorId: '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setConfiguration((previousValue) => ({
      ...previousValue,
      [name]: value,
    }));
  };

  /**
   * Function to reset configuration input
   */
  const handleResetInput = () => {
    setModalBoxStatus('close');
    setConfiguration({
      devices: '',
      connectorId: '',
    });
  };

  /**
   * Function to submit configuration
   */
  const handleSubmit = async () => {
    if (configuration.devices.length > 0 && configuration.connectorId) {
      ocppEvents
        .anyCommand({
          type: ocppMessageType.RMT_START_TRANSACTION,
          body: {
            connectorId: Number(configuration.connectorId),
          },
          chargingPointSns: [configuration.devices],
        })
        .then(() => {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            rightCorner: '',
            content: (
              <SuccessNotification
                title="Remote start transaction Requested "
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={handleResetInput}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) =>
          dispatch(
            setNotificationMessage({
              status: true,
              message: error?.response?.data?.message || 'Something went wrong!',
              type: 'error',
              code: 0,
            }),
          ),
        );
    } else {
      dispatch(
        setNotificationMessage({
          status: true,
          message: 'Device & ConnectorId required!',
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  useEffect(() => {
    if (chargingPoints.length === 0) {
      dispatch(fetchAllCharingPoints());
    }
  }, []);

  return (
    <div className="change-configuration-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <PageTitle title="Charge Points with OCPP v1.6 (Remote start transaction)" />
      <div className="change-configuration-container">
        <div className="configuration-multi-select-wrap">
          <SelectField
            name="devices"
            inputLabel=""
            selectedValue={configuration.devices}
            placeholder="Select Device"
            onChangeValue={handleInputChange}
            valueRequired
            options={chargingPoints}
          />
          <div className="multiple-button">
            {/* <MyButton label="Select All" buttonType="submit" onClickFunc={handleSelectAll} />
            <MyButton label="Select None" buttonType="submit" onClickFunc={handleSelectNone} /> */}
          </div>
        </div>
        <div className="side-heading">
          <p>Parameters</p>
        </div>
        <div className="change-configuration-parameters">
          <div className="single-row">
            <CustomInput
              name="connectorId"
              inputLabel="Connector Id"
              type="text"
              placeholder="Enter connector Id"
              value={configuration.connectorId}
              onChangeValue={handleInputChange}
            />
          </div>
        </div>
        <div className="form-buttons">
          <MyButton label="Cancel" buttonType="back" onClickFunc={handleResetInput} />
          <MyButton label="Submit" buttonType="submit" onClickFunc={handleSubmit} />
        </div>
      </div>
    </div>
  );
}

export default RemoteStartTransaction;
